import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface ExternalLinkDialogProps {
  open: boolean;
  position: { top: number; left: number };
  onSubmit: (url: string, label: string) => void;
  onCancel: () => void;
}

export function ExternalLinkDialog({
  open,
  position,
  onSubmit,
  onCancel,
}: ExternalLinkDialogProps) {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const urlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setUrl("");
      setLabel("");
      // Focus URL input after render
      requestAnimationFrame(() => urlRef.current?.focus());
    }
  }, [open]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedUrl = url.trim();
      const trimmedLabel = label.trim() || trimmedUrl;
      if (!trimmedUrl) return;
      // Auto-add https:// if no protocol
      const finalUrl =
        trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")
          ? trimmedUrl
          : `https://${trimmedUrl}`;
      onSubmit(finalUrl, trimmedLabel);
    },
    [url, label, onSubmit],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel],
  );

  if (!open) return null;

  return createPortal(
    <div
      className="fixed z-50 bg-popover border border-border rounded-md shadow-lg p-3 min-w-[300px]"
      style={{ top: position.top, left: position.left }}
      onKeyDown={handleKeyDown}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="external-link-url" className="text-xs">
            URL
          </Label>
          <Input
            ref={urlRef}
            id="external-link-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="h-8 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="external-link-label" className="text-xs">
            Label (optional)
          </Label>
          <Input
            id="external-link-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Link text"
            className="h-8 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-7"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!url.trim()}
            className="h-7"
          >
            Insert
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
